import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { updateMapFilters } from "@/features/auth/interactiveSurveySlice";
import PropertyCard from "@/shared/components/PropertyCard";
import ShortlistFilterToggle from "@/shared/components/ShortlistFilterToggle";
import { hasAddressLine2AsCustom, renderUrlOrValue } from "@/shared/utils";
import { getActivePropertyFields } from "@/shared/utils/propertyFieldRules";
import { getPropertyValue } from "@/shared/utils/propertyUtils";
import { Fragment, ReactNode, useRef } from "react";
import { useParams } from "react-router";

/**
 * Interactive Survey Comparison Page
 * Property comparison using grid layout with configurable fields
 */
const InteractiveSurveyComparison = () => {
  // Survey ID is used by the parent layout component to fetch survey data
  useParams<{ surveyId: string }>();
  const dispatch = useAppDispatch();
  const { currentSurvey, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );

  // Use Redux state for shortlist filter
  const showShortlistedOnly = mapFilters.showShortlistOnly;

  // Refs for scroll handling
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle shortlist filter toggle - dispatch to Redux
  const handleShortlistToggle = (value: boolean) => {
    dispatch(updateMapFilters({ showShortlistOnly: value }));
  };

  // Filter properties based on shortlist toggle
  // React Compiler automatically optimizes this computation
  if (!currentSurvey?.properties) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading survey data...</p>
        </div>
      </div>
    );
  }

  // Filter out hidden properties
  const filteredProperties = currentSurvey.properties
    .filter((p) => !p.isHidden)
    // Filter out current location properties if showCurrentLocation is false
    .filter(
      (p) =>
        currentSurvey.showCurrentLocation !== false || !p.isCurrentLocation,
    )
    // Apply shortlist filter if enabled
    .filter((p) => !showShortlistedOnly || p.isClientShortlist);

  // Extract active property fields from survey configuration
  // React Compiler automatically optimizes this computation
  const activePropertyFields = getActivePropertyFields(currentSurvey, {
    excludeAddressFields: true, // Always exclude address fields in comparison view
    hasAddressLine2Custom: hasAddressLine2AsCustom(currentSurvey),
  });

  // Format value based on data type and field key
  const formatFieldValue = (
    value: unknown,
    fieldKey?: string | null,
  ): ReactNode => {
    if (value === undefined || value === null || value === "") {
      return "—";
    }

    if (typeof value === "string") {
      return renderUrlOrValue(value, fieldKey);
    }

    return String(value);
  };

  return (
    <div className="flex h-full bg-white">
      <div ref={containerRef} className="flex-1 mt-4 overflow-auto">
        {/* Header Section - Fixed */}
        <div className="sticky top-0 z-20 bg-white">
          <div className="grid grid-flow-col auto-cols-[minmax(0,180px)] md:auto-cols-[minmax(0,240px)] min-w-max">
            {/* Label Column Header */}
            <div className="sticky left-0 z-30 bg-white px-6 py-2 content-baseline">
              <div className="mt-2">
                <ShortlistFilterToggle
                  onToggle={handleShortlistToggle}
                  shortlistedCount={
                    currentSurvey?.properties?.filter(
                      (p) => p.isClientShortlist && !p.isHidden,
                    ).length || 0
                  }
                />
              </div>
            </div>

            {/* Property Cards */}
            {filteredProperties.map((property) => (
              <Fragment key={property.id}>
                <PropertyCard
                  key={property.id}
                  property={property}
                  classStyleName="prop-card-col bg-white"
                  imageClassName="h-full"
                  showActions={true}
                  actionButtonClassName="bg-white bg-opacity-90 backdrop-blur-sm"
                />
              </Fragment>
            ))}
          </div>
        </div>

        {/* Body Section - Scrollable */}
        <div className="bg-white">
          {activePropertyFields.map((field, rowIndex) => (
            <div
              key={field.fieldId}
              className={`grid grid-flow-col auto-cols-[minmax(0,180px)] md:auto-cols-[minmax(0,240px)] min-w-max align-baseline ${
                rowIndex % 2 === 0 ? "bg-white" : "bg-[#F5F7F7]"
              } ${rowIndex === 0 ? "border-t border-gray-300" : ""}`}
            >
              {/* Field Label (Sticky) */}
              <div
                className={`sticky left-0 z-30 p-2 pl-6 prop-prop-value ${
                  rowIndex % 2 === 0 ? "bg-[#F5F7F7]" : "bg-[#E6EAEA]"
                }`}
              >
                {field.fieldLabel}
              </div>

              {/* Property Values */}
              {filteredProperties.map((property) => {
                const fieldKey = field.fieldKey || "";
                const fieldId = field.fieldId || "";
                // Only try to get property value if fieldKey exists, otherwise show "—"
                const fieldValue = getPropertyValue(
                  fieldKey,
                  fieldId,
                  property,
                );

                return (
                  <div
                    key={`${property.id}-${field.fieldId}`}
                    className="p-2 prop-prop-value wrap-break-word whitespace-pre-line"
                  >
                    {formatFieldValue(fieldValue, fieldKey)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveSurveyComparison;
